import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "marstick_theme";
const LEGACY_KEY = "theme";
const DEFAULT_THEME = "light";
const ThemeContext = createContext(null);

function getSystemTheme() {
  if (typeof window === "undefined" || !window.matchMedia) return DEFAULT_THEME;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function getStoredTheme() {
  if (typeof window === "undefined") return null;
  const stored = localStorage.getItem(STORAGE_KEY) || localStorage.getItem(LEGACY_KEY);
  return stored === "dark" || stored === "light" ? stored : null;
}

function getInitialTheme() {
  return getStoredTheme() || getSystemTheme();
}

function applyTheme(theme) {
  if (typeof document === "undefined") return;
  document.documentElement.classList.remove("light", "dark");
  document.documentElement.classList.add(theme);
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (getStoredTheme()) return;
    const media = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!media) return;
    const handleChange = (event) => setThemeState(event.matches ? "dark" : "light");
    media.addEventListener?.("change", handleChange);
    return () => media.removeEventListener?.("change", handleChange);
  }, []);

  const setTheme = useCallback((nextTheme) => {
    setThemeState((current) => {
      const resolved = typeof nextTheme === "function" ? nextTheme(current) : nextTheme;
      if (resolved !== "dark" && resolved !== "light") return current;
      localStorage.setItem(STORAGE_KEY, resolved);
      localStorage.setItem(LEGACY_KEY, resolved);
      return resolved;
    });
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((current) => {
      const resolved = current === "dark" ? "light" : "dark";
      localStorage.setItem(STORAGE_KEY, resolved);
      localStorage.setItem(LEGACY_KEY, resolved);
      return resolved;
    });
  }, []);

  const value = useMemo(() => ({ theme, setTheme, toggleTheme, isDark: theme === "dark" }), [theme, setTheme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext) || { theme: DEFAULT_THEME, setTheme: () => {}, toggleTheme: () => {}, isDark: false };
}